import { Button, Form, Input, Modal, Table, message } from "antd";
import axios from "axios";
import moment from "moment";
import React, { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { serverUrl } from "../../../nestserver";
import { numberInputGuard } from "../../../components/inputs/numberInputGuard";

const authHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

/**
 * School groups (trusts/societies that run several branches). A group is purely
 * an access-control grouping — branches keep their own fees, classes and
 * calendars. Its only effect is letting an admin placed in the group act inside
 * any of its schools.
 */
const GroupHome = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [addGroup, setAddGroup] = useState(false);

  const groups = useQuery("school-groups", async () => {
    const res = await axios.get(`${serverUrl}/school-group`, {
      headers: authHeader(),
    });
    return res.data;
  });

  const onFinish = async (data) => {
    try {
      const payload = {
        name: data?.name,
        owner: {
          name: data?.owner_name || "",
          address: data?.owner_address || "",
          phone: data?.owner_phone || "",
          email: data?.owner_email || "",
        },
      };
      const res = await axios.post(`${serverUrl}/school-group`, payload, {
        headers: authHeader(),
      });
      queryClient.invalidateQueries("school-groups");
      form.resetFields();
      setAddGroup(false);
      message.success(res?.data?.result?.message || "Group created");
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Failed to create group"
      );
    }
  };

  const columns = [
    {
      title: "Group Id",
      key: "refId",
      align: "center",
      render: (record) => (
        <div className="text-base font-semibold text-center">
          {record?.refId}
        </div>
      ),
    },
    {
      title: "Name",
      key: "name",
      align: "center",
      render: (record) => (
        <div className="text-base font-semibold text-center capitalize">
          {record?.name}
        </div>
      ),
    },
    {
      title: "Branches",
      key: "branches",
      align: "center",
      render: (record) => (
        <div className="text-base font-semibold text-center">
          {record?.schools?.length || 0}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <div
          className="text-base font-semibold text-center text-blue-500 cursor-pointer"
          onClick={() =>
            navigate(`/dashboard/super-admin/group/${record?._id}`)
          }
        >
          Manage
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className="bg-white drop-shadow-md p-8 w-full"
        >
          <div className="flex justify-between items-center">
            <div className="text-xl font-bold text-black">School Groups</div>
            <Button
              size="large"
              type="primary"
              className="text-white"
              onClick={() => setAddGroup(true)}
            >
              Add
            </Button>
          </div>
          <div className="py-5">
            <Table
              rowKey="_id"
              columns={columns}
              dataSource={
                groups?.isSuccess
                  ? [...(groups?.data?.result?.data || [])].sort((a, b) =>
                      moment(a?.createdAt).isBefore(b?.createdAt) ? 1 : -1
                    )
                  : []
              }
              pagination={true}
              loading={groups?.isLoading}
            />
          </div>
        </div>
      </div>

      <Modal
        width={600}
        visible={addGroup}
        onCancel={() => {
          setAddGroup(false);
          form.resetFields();
        }}
        footer={null}
      >
        <div className="py-2">
          <div className="text-2xl font-bold text-black pb-4">
            Add School Group
          </div>
        </div>
        <Form
          form={form}
          layout="vertical"
          name="add-group"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Group Name"
            name="name"
            rules={[{ required: true, message: "Please input the group name" }]}
          >
            <Input placeholder="e.g. Sunrise Group of Schools" />
          </Form.Item>

          <div className="text-lg font-semibold pb-2">Owner / Trust</div>
          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="owner_name" label="Name">
              <Input />
            </Form.Item>
            <Form.Item
              name="owner_phone"
              label="Phone"
              rules={[
                {
                  pattern: /^[0-9]{10}$/,
                  message: "Please enter a valid 10-digit phone number!",
                },
              ]}
              getValueFromEvent={(e) => e.target.value.slice(0, 10)}
            >
              <Input type="number" maxLength={10} min={0} {...numberInputGuard} />
            </Form.Item>
            <Form.Item
              name="owner_email"
              label="Email"
              rules={[{ type: "email", message: "Please enter a valid email" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="owner_address" label="Address">
              <Input />
            </Form.Item>
          </div>

          <div className="flex justify-end items-center pt-6">
            <div className="inline-flex items-center space-x-2">
              <Button
                size="large"
                onClick={() => {
                  setAddGroup(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                className="text-white"
              >
                Submit
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default GroupHome;
